import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import TrialBanner from '../../components/broker/TrialBanner';
import { ChevronDown, ChevronUp, CheckCircle, Clock, XCircle, Eye } from 'lucide-react';

interface Document {
  id: string;
  name: string;
  type: string;
  file_url: string;
  status: 'pending' | 'verified' | 'rejected';
  uploaded_at: string;
  reviewed_at?: string;
  admin_notes?: string;
  user_id?: string;
}

interface CarrierWithDocuments {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  documents: Document[];
}

const BrokerDocumentsPage = () => {
  const { profile } = useCompanyProfile();
  const [Carriers, setCarriers] = useState<CarrierWithDocuments[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCarrierId, setExpandedCarrierId] = useState<string | null>(null);
  const [reviewingDocId, setReviewingDocId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [changeModal, setChangeModal] = useState<{docId: string; currentStatus: string; userId?: string; docType?: string} | null>(null);

  const isTrialExpired =
    profile?.subscription_status === 'expired' ||
    (profile?.trial_ends_at && new Date(profile.trial_ends_at) < new Date());

  useEffect(() => {
    if (profile?.company_code) {
      fetchCarriersWithDocuments();
    }
  }, [profile?.company_code]);

  const fetchCarriersWithDocuments = async () => {
    try {
      setLoading(true);

      const { data: applications } = await supabase
        .from('applications')
        .select('user_id')
        .eq('company_code', profile?.company_code);

      if (!applications || applications.length === 0) {
        setCarriers([]);
        setLoading(false);
        return;
      }

      const userIds = [...new Set(applications.map(app => app.user_id))];

      const { data: carrierData, error } = await supabase
        .from('profiles')
        .select('*')
        .in('id', userIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const carriersWithDocs = await Promise.all(
        (carrierData || []).map(async (emp) => {
          const { data: docs } = await supabase
            .from('documents')
            .select('*')
            .eq('user_id', emp.id)
            .order('uploaded_at', { ascending: false });

          return {
            ...emp,
            documents: (docs || []).map(d => ({ ...d, user_id: emp.id })),
          };
        })
      );

      setCarriers(carriersWithDocs);
    } catch (err) {
      console.error('Error fetching carriers:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedCarrierId(expandedCarrierId === id ? null : id);
  };

  const handleReview = async (docId: string, newStatus: 'verified' | 'rejected', carrierId?: string, docType?: string) => {
    try {
      setReviewingDocId(docId);

      const { error } = await supabase
        .from('documents')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
          reviewed_by: profile?.id,
          admin_notes: reviewNotes || null,
        })
        .eq('id', docId);

      if (error) throw error;

      // Notify carrier — document approved or rejected
      if (carrierId) {
        if (newStatus === 'verified') {
          await supabase.from('notifications').insert({
            company_code: profile?.company_code,
            recipient_id: carrierId,
            title: 'Document Approved',
            message: `Your ${docType || 'document'} has been approved`,
            type: 'doc_approved',
          });
        } else {
          await supabase.from('notifications').insert({
            company_code: profile?.company_code,
            recipient_id: carrierId,
            title: 'Document Rejected',
            message: `Your ${docType || 'document'} was rejected${reviewNotes ? ': ' + reviewNotes : '. Please re-upload.'}`,
            type: 'doc_rejected',
          });
        }
      }

      await fetchCarriersWithDocuments();
      setReviewNotes('');
    } catch (err: any) {
      alert(err.message || 'Failed to update document');
    } finally {
      setReviewingDocId(null);
    }
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const handleViewDocument = (fileUrl: string) => {
    const publicUrl = `https://doflkphikufhrgsquyif.supabase.co/storage/v1/object/public/carrier-documents/${fileUrl}`;
    window.open(publicUrl, '_blank');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow">
        {Carriers.map((Carrier) => {
          const isExpanded = expandedCarrierId === Carrier.id;

          return (
            <div key={Carrier.id}>
              <div
                onClick={() => toggleExpand(Carrier.id)}
                className="p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center space-x-4">
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {Carrier.first_name} {Carrier.last_name}
                    </h3>
                    <p className="text-sm text-gray-600">{Carrier.email}</p>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 bg-gray-50">
                  {Carrier.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="bg-white rounded-lg border border-gray-200 p-4 mb-3"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {getStatusIcon(doc.status)}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{doc.name}</p>
                            <p className="text-sm text-gray-500">
                              {doc.type} • Uploaded {formatDate(doc.uploaded_at)}
                            </p>
                            {doc.admin_notes && (
                              <p className="text-xs text-gray-600 mt-1 italic">
                                Note: {doc.admin_notes}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDocument(doc.file_url);
                            }}
                            className="flex items-center px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </button>

                          {doc.status === 'pending' ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isTrialExpired) {
                                    alert('Trial expired.');
                                    return;
                                  }
                                  handleReview(doc.id, 'verified', Carrier.id, doc.type);
                                }}
                                className="px-3 py-1.5 text-sm text-white rounded bg-green-600 hover:bg-green-700"
                              >
                                Approve
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isTrialExpired) {
                                    alert('Trial expired.');
                                    return;
                                  }
                                  handleReview(doc.id, 'rejected', Carrier.id, doc.type);
                                }}
                                className="px-3 py-1.5 text-sm text-white rounded bg-red-600 hover:bg-red-700"
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <>
                              <span
                                className={`px-3 py-1.5 text-sm font-medium rounded ${
                                  doc.status === 'verified'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {doc.status === 'verified' ? '✓ Verified' : '✗ Rejected'}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setChangeModal({
                                    docId: doc.id,
                                    currentStatus: doc.status,
                                    userId: Carrier.id,
                                    docType: doc.type,
                                  });
                                }}
                                className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                              >
                                Change
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Change Status Modal */}
      {changeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Change Document Status</h3>
            <p className="text-sm text-gray-600 mb-4">
              Current status: <span className="font-medium">{changeModal.currentStatus}</span>
              <br />
              New status:{' '}
              <span className="font-medium">
                {changeModal.currentStatus === 'verified' ? 'rejected' : 'verified'}
              </span>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for change *
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Enter reason..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setChangeModal(null);
                  setReviewNotes('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!reviewNotes.trim()) {
                    alert('Please enter a reason');
                    return;
                  }
                  const newStatus =
                    changeModal.currentStatus === 'verified'
                      ? 'rejected'
                      : 'verified';
                  handleReview(changeModal.docId, newStatus, changeModal.userId, changeModal.docType);
                  setChangeModal(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerDocumentsPage;
